/**
 * LocalStorage utility functions with TypeScript generics
 */

/**
 * Save a value to localStorage
 * @param key - The localStorage key
 * @param value - The value to save (will be JSON stringified)
 */
export const saveToLocalStorage = <T extends string | number | boolean | null>(
  key: string,
  value: T,
): void => {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.warn("Failed to save to localStorage:", error);
  }
};

/**
 * Load a value from localStorage
 * @param key - The localStorage key
 * @param defaultValue - The default value to return if key doesn't exist or parsing fails
 * @returns The parsed value from localStorage or the default value
 */
export const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : defaultValue;
  } catch (error) {
    console.warn("Failed to load from localStorage:", error);
    return defaultValue;
  }
};

/**
 * Common localStorage keys used across the creative library
 */
export const CREATIVE_LIBRARY_STORAGE_KEYS = {
  ASPECT_RATIO: "creative-library-aspect-ratio",
  VARIATIONS: "creative-library-variations",
  AVOID_DANGER_ZONE: "creative-library-avoid-danger-zone",
  CREATIVE_TYPE: "creative-library-creative-type",
  MODEL: "creative-library-model",
  VIDEO_TYPE: "creative-library-video-type",
  DEFAULT_PROMPT_ID: "creative-library-default-prompt-id",
  MOODBOARD_ID: "creative-library-moodboard-id",
  UGC_VIDEO_EDITING_PROMPT_ID: "creative-library-ugc-video-editing-prompt-id",
} as const;
